import React from "react";
import Modal from "../modal";
import styles from "./dialog.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { togglePopup } from "../../../slices/utilsSlice";
import { RootState } from "../../../store";
import { useTranslation } from "react-i18next";

type DialogType = "success" | "error" | "warning" | "info" | "confirm";

function normalizeType(raw: string): DialogType {
  switch (raw.toLowerCase()) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
    case "information":
      return "info";
    case "confirm":
    case "confirmation":
      return "confirm";
    default:
      return "info";
  }
}

const titleKeys: Record<DialogType, string> = {
  success: "dialog.success",
  error: "dialog.error",
  warning: "dialog.warning",
  info: "dialog.information",
  confirm: "dialog.confirm",
};

const accentClasses: Record<DialogType, string> = {
  success: styles.accentSuccess,
  error: styles.accentError,
  warning: styles.accentWarning,
  info: styles.accentInfo,
  confirm: styles.accentConfirm,
};

const iconClasses: Record<DialogType, string> = {
  success: styles.iconSuccess,
  error: styles.iconError,
  warning: styles.iconWarning,
  info: styles.iconInfo,
  confirm: styles.iconConfirm,
};

const closeBtnClasses: Record<Exclude<DialogType, "confirm">, string> = {
  success: styles.closeBtnSuccess,
  error: styles.closeBtnError,
  warning: styles.closeBtnWarning,
  info: styles.closeBtnInfo,
};

const icons: Record<DialogType, React.ReactNode> = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  confirm: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

const Dialog: React.FC = () => {
  const dispatch = useDispatch();
  const popUp = useSelector((state: RootState) => state.utils.popUp);
  const { t } = useTranslation();

  const type = normalizeType(popUp.type);
  const isConfirm = type === "confirm";

  const dismiss = () => {
    dispatch(togglePopup({}));
  };

  const onCancel = () => {
    dismiss();
  };

  const onConfirm = () => {
    dismiss();
    if (popUp.callback) {
      popUp.callback();
    }
  };

  const onClose = () => {
    dismiss();
    if (popUp.callback && !isConfirm) {
      popUp.callback();
    }
  };

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isConfirm) {
      onClose();
    }
  };

  return (
    <Modal>
      <div className={styles.overlay} onClick={onOverlayClick}>
        <div className={styles.dialog} role="dialog" aria-modal="true">
          <div className={`${styles.accentBar} ${accentClasses[type]}`} />

          <div className={styles.header}>
            <button className={styles.closeX} onClick={isConfirm ? onCancel : onClose} aria-label={t("common.close", "Close")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className={styles.body}>
            <div className={`${styles.iconCircle} ${iconClasses[type]}`}>
              {icons[type]}
            </div>

            <div className={styles.title}>{t(titleKeys[type])}</div>
            <div className={styles.message}>{popUp.message}</div>

            {isConfirm ? (
              <div className={styles.actions}>
                <button className={styles.cancelBtn} onClick={onCancel}>
                  {t("cancel")}
                </button>
                <button className={styles.confirmBtn} onClick={onConfirm}>
                  {t("confirm")}
                </button>
              </div>
            ) : (
              <div className={styles.actions}>
                <button
                  className={`${styles.closeBtn} ${closeBtnClasses[type as Exclude<DialogType, "confirm">]}`}
                  onClick={onClose}
                  aria-label={t("common.close", "Close")}
                >
                  {t("close")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Dialog;
